import React, { useEffect, useMemo, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  buildCartBundleInstanceId,
  buildCartProductWithVariant,
  decrementQuantity,
  getCartProductAvailableQuantity,
  incrementQuantity,
  isCollectionCartProduct,
  normalizeAvailableQuantity,
  removeBundleFromCart,
  selectCartBundles,
  selectStandaloneCartItems,
  updateBundleItemVariant,
  updateStandaloneCartVariant,
} from "../store/cartSlice";
import toast from "react-hot-toast";
import CartBundleCard from "./CartBundleCard";
import { getCartBundlePricing } from "../utils/cartBundle";
import { ReactComponent as CartCloseIcon } from "./../assets/svgs/cart-close-icon.svg";
import { formatStorePrice, getProductPricing } from "../utils";
import QuickPayButton from "./QuickPayButton.js";

const AddedToCartDrawer = ({
  isOpen,
  productId,
  bundleId,
  bundleInstanceId,
  onClose,
  onCheckout,
  onViewProduct,
}) => {
  const dispatch = useDispatch();
  const standaloneCartItems = useSelector(selectStandaloneCartItems);
  const cartBundles = useSelector(selectCartBundles);
  const [activeProductId, setActiveProductId] = useState(productId);
  const [activeBundleInstanceId, setActiveBundleInstanceId] = useState(bundleInstanceId || bundleId || null);
  const [isEditingVariant, setIsEditingVariant] = useState(false);
  
  useEffect(() => {
    if (!isOpen) return;
    setActiveProductId(productId);
    setActiveBundleInstanceId(bundleInstanceId || bundleId || null);
    setIsEditingVariant(false);
  }, [isOpen,productId,bundleId,bundleInstanceId]);

  const bundleLookupId = activeBundleInstanceId;  const bundle = useMemo(() =>
    bundleLookupId
      ? cartBundles.find(
          (item) => String(item.instanceId || item.id) === String(bundleLookupId),
        )
      : null,
  [bundleLookupId, cartBundles]);
  const item = useMemo(() => {
    if (!activeProductId || bundle) return null;

    return standaloneCartItems.find(
      (cartItem) => Number(cartItem?.product?.variant_id ?? cartItem?.product?.id) === Number(activeProductId),
    );
  }, [standaloneCartItems,activeProductId, bundle]);
  const isBundle = Boolean(bundle);
  const bundlePricing = isBundle ? getCartBundlePricing(bundle) : null;

  const product = item?.product || {};
  const isExclusiveProduct = product?.productType === "exclusive" || product?.product_type === "exclusive";
  const quantity = Number(item?.quantity || 1);
  const bagCount = isBundle ? bundle.items.reduce(
      (total, bundleItem) =>
        total +
        Number(bundleItem?.quantity || 1),
      0,
    )
  : quantity;
  const collectionProduct = isCollectionCartProduct(product);
  const editableVariants = useMemo(() => {
    if (!collectionProduct) return [];
    return Array.isArray(product?.variants)
      ? product.variants.filter(
          (variant) =>
            Number.isFinite(Number(variant?.id)) &&
            Number(variant.id) > 0,
        )
      : [];
  }, [collectionProduct, product?.variants]);

  const canEditVariant = editableVariants.length > 1;
  const currentVariantId = Number(product?.variant_id ?? product?.id);
  const handleStandaloneVariantChange = (variant) => {
    const targetVariantId = Number(variant?.id);
    if (!Number.isFinite(targetVariantId) || targetVariantId <= 0) return;
    if (targetVariantId === currentVariantId) {
      setIsEditingVariant(false);
      return;
    }

    const availableQuantity = normalizeAvailableQuantity(variant?.quantity);

    if (variant?.isOutOfStock === true ||
      (availableQuantity !== null && availableQuantity < quantity)) {
      toast.error(`${variant?.size || "This variant"} does not have enough stock.`);
      return;
    }

    const targetItem = standaloneCartItems.find(
        (cartItem) => cartItem !== item &&
          Number(cartItem?.product?.variant_id ?? cartItem?.product?.id) === targetVariantId);

    if (targetItem) {
      const combinedQuantity = Number(targetItem.quantity || 1) + quantity;
      if (availableQuantity !== null && combinedQuantity > availableQuantity) {
        toast.error(`Only ${availableQuantity} available for this variant.`);
        return;
      }
    }

    setActiveProductId(targetVariantId);
    dispatch(updateStandaloneCartVariant({ currentVariantId, variant}));
    setIsEditingVariant(false);
    toast.success(variant?.size ? `Size changed to ${variant.size}.` : "Variant updated.",
    );
  };
  const pricing = getProductPricing(product, collectionProduct);
  const unitPrice = pricing.discountedPrice || 0;
  const subtotal = isBundle ? bundlePricing.currentSubtotal : unitPrice * quantity;
  const bundleDiscount = isBundle ? bundlePricing.bundleDiscount : 0;
  const shipping = 0;
  const tax = 0;
  const total = isBundle ? bundlePricing.total : subtotal + shipping + tax;
  const availableQuantity = getCartProductAvailableQuantity(product);
  const hasKnownStock = availableQuantity !== null;
  const reachedStockLimit = hasKnownStock && quantity >= availableQuantity;
  const image =
    product?.image || product?.image_url || product?.main_images?.[0] || "";

  // Same shape /cart-checkout/ and the other quick-pay placements expect —
  // this drawer only ever holds the one item just added.
  const quickPayItems = useMemo(() => {
    if (!item) return [];
    return [
      {
        variant_id: Number(product?.variant_id || product?.id),
        quantity,
      },
    ];
  }, [item, product?.variant_id, product?.id, quantity]);

  const quickPayBundles = useMemo(() => {
    if (!bundle?.id || !Array.isArray(bundle?.items) || !bundle.items.length) {
      return [];
    }

    const variantIds = bundle.items
      .map((bundleItem) =>
        Number(
          bundleItem?.product?.variant_id ||
            bundleItem?.product?.id,
        ),
      )
      .filter((variantId) => Number.isFinite(variantId) && variantId > 0);

    if (variantIds.length !== bundle.items.length) return [];

    const numericBundleId = Number(bundle.id);
    if (!Number.isFinite(numericBundleId) || numericBundleId <= 0) return [];

    return [
      {
        bundle_id: numericBundleId,
        quantity: Math.max(1,Number(bundle?.quantity || 1)),
        variant_ids: variantIds,
      },
    ];
  }, [bundle]);

  // Cents, and must match what the backend recomputes from the DB — same
  // total already shown just above.
  const quickPayAmount = Math.round(Number(total) * 100);

  const hasDrawerContent = Boolean(item || bundle);
  useEffect(() => {
    if (!isOpen || !hasDrawerContent) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, hasDrawerContent, onClose]);

  if (!isOpen || !hasDrawerContent) {
    return null;
  }

  const handleBundleVariantChange = ( productToEdit, variant) => {
  if (!bundle) return;

  const currentBundleInstanceId = String(bundle.instanceId || bundle.id || "").trim();
  const currentVariantId = Number(productToEdit?.variant_id ?? productToEdit?.id);
  const targetVariantId = Number(variant?.id);

  if (!Number.isFinite(targetVariantId) || targetVariantId <= 0) return;
  if (targetVariantId === currentVariantId) return;
  const nextProduct = buildCartProductWithVariant(productToEdit, variant);
  if (!nextProduct) return;
  const bundleQuantity = Math.max(1,Number(bundle?.quantity || 1));
  const availableQuantity = getCartProductAvailableQuantity(nextProduct);

  let reservedOutsideBundle = 0;
  standaloneCartItems.forEach((cartItem) => {
    const variantId = Number(cartItem?.product?.variant_id ?? cartItem?.product?.id);

    if (variantId === targetVariantId) {
      reservedOutsideBundle += Math.max(1, Number(cartItem.quantity || 1));
    }
  });

  cartBundles.forEach((cartBundle) => {
    const instanceId = String(cartBundle?.instanceId || cartBundle?.id || "");

    if (instanceId === currentBundleInstanceId) return;

    cartBundle.items?.forEach((cartItem) => {
      const variantId = Number(cartItem?.product?.variant_id ?? cartItem?.product?.id);

      if (variantId === targetVariantId) {
        reservedOutsideBundle += Math.max(1,Number(cartItem.quantity || 1));
      }
    });
  });

  if (availableQuantity !== null && reservedOutsideBundle +
      bundleQuantity > availableQuantity) {
    toast.error(`${variant?.size || "This variant"} does not have enough stock.`);
    return;
  }

  const previewItems = bundle.items.map(
    (bundleItem) => {
      const variantId = Number(
        bundleItem?.product?.variant_id ??
        bundleItem?.product?.id,
      );

      if (variantId !== currentVariantId) return bundleItem;

      return {
        ...bundleItem,
        product: nextProduct,
      };
    },
  );

  const nextInstanceId = buildCartBundleInstanceId(bundle, previewItems);

  const duplicateBundle = cartBundles.some(
      (cartBundle) =>
        String(cartBundle.instanceId || cartBundle.id) === nextInstanceId &&
        String(cartBundle.instanceId || cartBundle.id) !== currentBundleInstanceId,
    );

  if (duplicateBundle) {
    toast.error("This bundle configuration is already in your cart.");
    return;
  }

  setActiveBundleInstanceId(nextInstanceId);

  dispatch(updateBundleItemVariant({
      bundleInstanceId: currentBundleInstanceId,
      currentVariantId,
      variant,
    }));

  toast.success(
    variant?.size
      ? `${productToEdit.name} changed to size ${variant.size}.`
      : `${productToEdit.name} updated.`,
  );
};

  return (
    <div className="fixed inset-0 z-[10000]">
      {/* Dark background overlay */}
      <button
        type="button"
        aria-label="Close cart"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/50"
      />

      {/* Drawer */}
      <aside className="absolute right-0 top-0 flex h-[100dvh] w-full flex-col bg-white shadow-[-8px_0_30px_rgba(0,0,0,0.12)] sm:w-[420px] lg:w-[450px] p-[10px]">
        <div className="flex h-[64px] shrink-0 items-center justify-between px-5 border-b border-[#E2E2DC]">
          <h2 className="font-helvena text-[18px] md:text-[22px] font-normal text-[#111111]">
            Your Bag ({bagCount})
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-[8px] text-[#666660] transition-opacity hover:opacity-60"
          >
            <span className="font-inter text-[12px] uppercase">Close</span>
            <CartCloseIcon />
          </button>
        </div>

        {/* CURRENT PRODUCT ONLY */}
        <div className="min-h-0 flex-1 overflow-y-auto py-[10px]">
        {isBundle ? (
            <CartBundleCard
              bundle={bundle}
              compact
              onViewProduct={onViewProduct}
              onVariantChange={handleBundleVariantChange}
              onRemove={() => {
                dispatch(removeBundleFromCart(bundle.instanceId || bundle.id));
                onClose?.();
                toast.success("Bundle removed from cart.");
              }}
            />
          ) : (
          <div className="flex items-start rounded-[5px] border border-[#DFDFDF] bg-white py-3 px-5">
            {/* Product image */}
            <div
              className={`${pricing.hasDiscount ? "h-[147px]" : "h-[129px]"} w-[94px] shrink-0 overflow-hidden rounded-[5px] bg-[#D6D6D6]`}
            >
              {image ? (
                <img
                  src={image}
                  alt={product?.name || "Product"}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full bg-[#D6D6D6]" />
              )}
            </div>

            {/* Product information */}
            <div className="ml-3 flex min-w-0 flex-1 flex-col">
              {product?.brand ? (
                <p className="truncate text-[14px] font-medium text-black font-helvena">
                  {product.brand}
                </p>
              ) : null}
              <p className="font-helvena mt-[3px] line-clamp-2 text-[11px] leading-[14px] text-[#1A1A26]">
                {product?.name}
              </p>

              {/* Variant information */}
              <div className="mt-[10px] font-spaceMono text-[12px] leading-[18px] text-[#3D648B]">
                <div className="flex items-start justify-between gap-3">
                  <div>
                {product?.size ? <p>Size: {product.size}</p> : null}
                {product?.color ? <p>Colour: {product.color}</p> : null}
                  </div>
                </div>

                {isEditingVariant ? (
                  <div className="mt-3 rounded-[5px] border border-[#E2E2DC] bg-[#FAFAF8] p-2">
                    <p className="mb-2 font-spaceMono text-[9px] uppercase tracking-[0.08em] text-[#777777]">
                      Select Variant
                    </p>

                    <div className="flex flex-wrap gap-1.5">
                      {editableVariants.map((variant) => {
                        const variantId = Number(variant.id);
                        const available = normalizeAvailableQuantity(variant?.quantity);
                        const isSelected = variantId === currentVariantId;
                        const isUnavailable = variant?.isOutOfStock === true || (available !== null && available < quantity);

                        return (
                          <button
                            key={variantId}
                            type="button"
                            disabled={isSelected || isUnavailable}
                            onClick={() => handleStandaloneVariantChange(variant)}
                            className={[
                              "min-w-[46px] rounded-[4px] border px-2.5 py-1.5",
                              "font-spaceMono text-[10px] transition-colors",
                              isSelected
                                ? "border-black bg-black text-white"
                                : "border-[#D8D5D0] bg-white text-[#313131]",
                              isUnavailable
                                ? "cursor-not-allowed opacity-35"
                                : !isSelected
                                  ? "hover:border-black"
                                  : "",
                            ].join(" ")}
                          >
                            {variant?.size || `#${variantId}`}
                            {variant?.color
                              ? ` / ${variant.color}`
                              : ""}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              <div className="mt-1 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => onViewProduct?.(product)}
                  className="font-helvena text-[11px] text-[#595973] underline underline-offset-2"
                >
                  View Product
                </button>
                {canEditVariant ? (
                  <>
                    <span className="text-[11px] text-[#B8B8B8]">|</span>
                    <button
                      type="button"
                      onClick={() => setIsEditingVariant((current) => !current)}
                      className="font-helvena text-[11px] text-[#595973] underline underline-offset-2"
                    >
                      {isEditingVariant ? "Cancel" : "Edit"}
                    </button>
                  </>
                ) : null}
              </div>
              </div>

              {/* Quantity / price */}
              <div className="flex items-end justify-between font-spaceMono">
                <div className="flex items-center gap-[16px] text-[13px] text-[#333333]">
                  <button
                    type="button"
                    disabled={quantity <= 1}
                    onClick={() => dispatch(decrementQuantity(product.id))}
                    className=" text-[17px] leading-none disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    −
                  </button>
                  <span className="min-w-[12px] text-center text-[14px]">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    disabled={reachedStockLimit}
                    onClick={() => dispatch(incrementQuantity(product.id))}
                    className=" text-[17px] leading-none disabled:cursor-not-allowed disabled:opacity-30"
                  >
                    +
                  </button>
                </div>

                <div className="text-right">
                  {pricing.hasDiscount ? (
                    <p className="font-helvena text-[12px] text-[#909090] line-through">
                      {formatStorePrice(pricing.originalPrice * quantity)}
                    </p>
                  ) : null}
                  <p className="font-helvena text-[20px] font-normal text-[#525252]">
                    {formatStorePrice(subtotal)}
                  </p>
                  {isBundle && bundleDiscount > 0 ? (
                    <div className="mt-3 flex items-center justify-between font-helvena text-[11px] uppercase tracking-[1.05px] text-[#257A45]">
                      <span>
                        Bundle Savings ({bundle.discountPercent}%)
                      </span>
                      <span>
                        -{formatStorePrice(bundleDiscount)}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>

              {reachedStockLimit ? (
                <p className=" mt-2 font-spaceMono text-[8px] text-[#B42318]">
                  {isExclusiveProduct
                    ? "Maximum quantity reached"
                    : `Maximum ${availableQuantity} available`}
                </p>
              ) : null}
            </div>
          </div>
        )}
        </div>

        {/* CHECKOUT SECTION */}
        <div className="shrink-0 bg-white">
          <div className="rounded-[4px] border border-[#00000033] p-4">
            <p className="mb-4 font-spaceMono text-[11px] font-bold uppercase tracking-[2px] text-[#6A7282]">
              &gt; Checkout
            </p>

            <div className="rounded-[4px] border border-[#0000001A] px-3 py-3">
              <div className="flex items-center justify-between font-helvena text-[11px] uppercase tracking-[1.05px] text-[#6A7282]">
                <span>Subtotal</span>
                <span>{formatStorePrice(subtotal)}</span>
              </div>
              {isBundle && bundleDiscount > 0 ? (
                <div className="mt-3 flex items-center justify-between font-helvena text-[11px] uppercase tracking-[1.05px] text-[#257A45]">
                  <span>
                    Bundle Savings (
                    {bundle.discountPercent}%)
                  </span>
                  <span>
                    -{formatStorePrice(bundleDiscount)}
                  </span>
                </div>
              ) : null}

              <div className="mt-3 flex items-center justify-between font-helvena text-[11px] uppercase tracking-[1.05px] text-[#6A7282]">
                <span>Shipping (Standard)</span>
                <span>{formatStorePrice(shipping)}</span>
              </div>

              <div className="mt-3 flex items-center justify-between font-helvena text-[11px] uppercase tracking-[1.05px] text-[#6A7282]">
                <span>Tax</span>

                <span>{formatStorePrice(tax)}</span>
              </div>
            </div>

            {/* Total */}
            <div className="mt-3 flex h-[38px] items-center justify-between rounded-[4px] border border-[#0000001A] px-3">
              <span className="font-spaceMono text-[11px] font-semibold uppercase tracking-[0.08em] text-[#626F82]">
                Total
              </span>
              <span className="text-[13px] font-semibold text-[#626F82]">
                {formatStorePrice(total)}
              </span>
            </div>

            {/* Checkout */}
            <button
              type="button"
              onClick={() =>
                onCheckout?.({
                  productId: isBundle
                    ? null
                    : Number(product?.variant_id ?? product?.id,),
                  bundleId: isBundle ? bundle?.id : null,
                  bundleInstanceId: isBundle
                    ? bundle?.instanceId
                    : null,
                })
              }              
              className="font-helvena mt-3 flex h-[42px] w-full items-center justify-center rounded-[5px] bg-[#252525] text-[14px] text-white transition-opacity hover:opacity-95"
            >
              Checkout
            </button>

            {/* Express pay */}
            <div className="mt-3 flex items-center justify-center gap-2">
              <span className="text-[10px] font-semibold text-black text-center">
                Express pay
              </span>
            </div>

            <div className="mt-3 grid grid-cols-1">
              {/*
                * Express checkout (Apple Pay). Renders itself only when the
                * browser actually has a wallet, and skips both the cart and
                * checkout — the wallet supplies the email and the address.
                */}
              <QuickPayButton
                express
                className="w-full min-w-0"
                items={quickPayItems}
                bundles={quickPayBundles}
                amount={quickPayAmount}
                disabled={
                  isBundle
                    ? quickPayBundles.length === 0
                    : quickPayItems.length === 0
                }
                buttonHeight={40}
                buttonRadius={5}
                applePayTheme="black"
              />
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default AddedToCartDrawer;
